import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import { EventCard, cardGrid } from "../components/Cards";
import { Pagination } from "../components/Pagination";
import { GENRES } from "../lib/constants";
import { useTerms } from "../lib/useTerms";
import { useSettings } from "../context/SettingsContext";

const STATES = ["", "Florida", "Alabama", "Mississippi", "Louisiana", "Texas"];

export default function Events() {
  const { settings } = useSettings();
  const cs = settings?.card_styles?.events || "default";
  const [sp, setSp] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const genres = useTerms("event", "genre", GENRES);
  const eventTypes = useTerms("event", "event_type", ["Concert", "Festival"]);
  const [f, setF] = useState({
    q: sp.get("q") || "", state: sp.get("state") || "", city: sp.get("city") || "",
    genre: sp.get("genre") || "", event_type: "", date_from: "", date_to: "", free: false, family_friendly: false,
  });

  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 12, sort: "newest" });
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    const { data } = await api.get(`/events?${p.toString()}`);
    setEvents(data.items); setMeta(data); setLoading(false);
  };

  useEffect(() => {
    const t = setTimeout(load, 250); return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [f, page]);

  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setPage(1); };
  const today = new Date().toISOString().slice(0, 10);
  const weekend = () => { set("date_from", today); const d = new Date(); d.setDate(d.getDate() + 7); set("date_to", d.toISOString().slice(0, 10)); };

  const sel = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-rose-500";

  return (
    <div className="px-6 md:px-12 lg:px-20 py-10" data-testid="events-page">
      <p className="overline mb-2">Live across the coast</p>
      <h1 className="font-display font-extrabold text-4xl md:text-5xl mb-8">Events Directory</h1>
      <div className="grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-20 bg-[#111] border border-white/10 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 font-semibold"><SlidersHorizontal size={16} /> Filters</div>
            <input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search keyword" className={sel} data-testid="filter-q" />
            <div className="flex gap-2">
              <button onClick={() => { set("date_from", today); set("date_to", today); }} className="flex-1 text-xs border border-white/10 rounded-lg py-2 hover:bg-white/5" data-testid="filter-today">Today</button>
              <button onClick={weekend} className="flex-1 text-xs border border-white/10 rounded-lg py-2 hover:bg-white/5" data-testid="filter-weekend">This week</button>
            </div>
            <div>
              <label className="text-xs text-zinc-400">From</label>
              <input type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} className={sel} data-testid="filter-date-from" />
            </div>
            <div>
              <label className="text-xs text-zinc-400">To</label>
              <input type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} className={sel} data-testid="filter-date-to" />
            </div>
            <select value={f.state} onChange={(e) => set("state", e.target.value)} className={sel} data-testid="filter-state">{STATES.map((s) => <option key={s} value={s}>{s || "All States"}</option>)}</select>
            <input value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="City" className={sel} data-testid="filter-city" />
            <select value={f.genre} onChange={(e) => set("genre", e.target.value)} className={sel} data-testid="filter-genre"><option value="">All Genres</option>{genres.map((s) => <option key={s} value={s}>{s}</option>)}</select>
            <select value={f.event_type} onChange={(e) => set("event_type", e.target.value)} className={sel} data-testid="filter-type"><option value="">All Types</option>{eventTypes.map((s) => <option key={s} value={s}>{s}</option>)}</select>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={f.free} onChange={(e) => set("free", e.target.checked)} data-testid="filter-free" /> Free events</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={f.family_friendly} onChange={(e) => set("family_friendly", e.target.checked)} data-testid="filter-family" /> Family friendly</label>
          </div>
        </aside>
        <div className="lg:col-span-9">
          <p className="text-sm text-zinc-400 mb-5">{loading ? "Loading..." : `${meta.total} events found`}</p>
          <div className={`grid gap-5 ${cardGrid(cs)}`}>
            {events.map((e) => <EventCard key={e.id} e={e} style={cs} />)}
          </div>
          {!loading && events.length === 0 && <p className="text-zinc-500 py-20 text-center">No events match your filters.</p>}
          <Pagination page={page} pages={meta.pages} onPage={setPage} />
        </div>
      </div>
    </div>
  );
}
